/**
 * MIT License
 * Copyright (c) 2023 Jamasp Team
 */
import { useEffect, useCallback, useState } from 'react';
import EmailRegistrationStepper from '../components/register/EmailRegistrationStepper';
import { useAlert } from '../context/AlertContext';
import { useRegistration } from '../context/RegistrationContext';
import { checkProjectForRegistration } from '../services/RegistrationService';

const blue = '#0A66C2';
const gray = { border: '#D6E2EB', bg: '#FBFCFE', dark: '#131B20', mid: '#4C5967' };

export default function EmailRegistration() {
  const {
    email, setEmail, projectId, activeStep, loading,
    storeEmail, participantId, initiateFitbitAuth,
    renewRegistration, handleError, verifyEmail,
  } = useRegistration();

  const { addAlert } = useAlert();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const checkProjectStatus = async () => {
      if (!projectId) return;
      try {
        const result = await checkProjectForRegistration(projectId);
        setIsOpen(result);
        if (!result) console.error('Check project status failed', result);
      } catch (err) {
        handleError(err, 'Error checking project status. Please try again.');
      }
    };
    checkProjectStatus();
  }, [projectId, addAlert, handleError]);

  const handleEmailChange     = useCallback((v) => setEmail(v), [setEmail]);
  const handleStoreEmail      = useCallback(() => storeEmail(), [storeEmail]);
  const handleInitiateFitbit  = useCallback(() => initiateFitbitAuth(), [initiateFitbitAuth]);

  const shell = (children) => (
    <div style={{
      minHeight: '100vh', background: gray.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'DM Sans', sans-serif", padding: '24px 16px',
    }}>
      <div style={{
        background: '#fff', border: `1px solid ${gray.border}`, borderRadius: 12,
        padding: '36px 40px', width: '100%', maxWidth: 480,
        boxShadow: '0 4px 24px rgba(9,89,170,0.07)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 18, fontWeight: 500, color: blue, letterSpacing: '0.5px' }}>
            jamasp
          </span>
        </div>
        {children}
      </div>
    </div>
  );

  if (!isOpen) return shell(
    <p style={{ fontSize: 15, color: gray.mid, textAlign: 'center' }}>
      This project is not currently open for registration.
    </p>
  );

  if (loading) return shell(
    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', border: `3px solid ${gray.border}`, borderTopColor: blue, animation: 'ja-spin 0.8s linear infinite' }} />
      <style>{`@keyframes ja-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return shell(
    <EmailRegistrationStepper
      email={email}
      activeStep={activeStep}
      participantId={participantId}
      loading={loading}
      onEmailChange={handleEmailChange}
      onStoreEmail={handleStoreEmail}
      onInitiateFitbitAuth={handleInitiateFitbit}
      onRenewRegistration={renewRegistration}
      onVerifyEmail={verifyEmail}
    />
  );
}
